import {
  Avatar,
  Button as MuiButton,
  ClickAwayListener,
  styled,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import { Button } from '../../../../components/buttons'
import { SelectOption } from '../../../../model/selectOption'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'
import { ProjectMemberSearchOption } from '../../../../../domain/value-object/ProjectMemberSearchOption'
import { useProjectMemberRepository } from '../../../../../services/adaptors/projectMemberRepositoryAdaptor'
import {
  ProjectMemberPopper,
  ProjectMemberPopperHandle,
} from '../../../../features/ProjectMemberPopper'
import { ProjectMemberInput } from '../../../../features/ProjectMemberInput'
import { useDebounce } from '../../../../hooks/useDebounce'

type ProjectMemberSearchConditionAutocompleteInputProps = {
  value: string[] | undefined
  fetchSelectedOption: (value: string) => Promise<SelectOption | undefined>
  title: string
  onChange: (newValue: string[] | undefined) => void
  memberOptions: ProjectMemberSearchOption[]
}

export const ProjectMemberSearchConditionAutocompleteInput = ({
  value,
  fetchSelectedOption,
  title,
  onChange,
  memberOptions,
}: ProjectMemberSearchConditionAutocompleteInputProps) => {
  const [isPopperOpen, setIsPopperOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [searchedOptions, setSearchedOptions] = useState<
    ProjectMemberSearchOption[]
  >([])
  const debouncedSearchText = useDebounce(searchText, 300)

  const anchorRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popperRef = useRef<ProjectMemberPopperHandle>(null)

  const { searchAsProjectMemberSearchOption } = useProjectMemberRepository()

  useEffect(() => {
    if (!isPopperOpen || debouncedSearchText === '') return
    let isMounted = true
    const fn = async () => {
      const result =
        await searchAsProjectMemberSearchOption(debouncedSearchText)
      if (isMounted) {
        setSearchedOptions(result)
      }
    }
    fn()
    return () => {
      isMounted = false
    }
  }, [isPopperOpen, debouncedSearchText, searchAsProjectMemberSearchOption])

  const displayOptions = searchText === '' ? memberOptions : searchedOptions

  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([])
  useEffect(() => {
    if (!value || value.length === 0) {
      setSelectedOptions([])
      return
    }
    let isMounted = true
    Promise.all(value.map(v => fetchSelectedOption(v))).then(options => {
      if (!isMounted) return
      setSelectedOptions(
        options.filter((o): o is SelectOption => o !== undefined)
      )
    })
    return () => {
      isMounted = false
    }
  }, [value, fetchSelectedOption])

  const closePopper = useCallback(() => {
    setIsPopperOpen(false)
    setSearchText('')
  }, [])

  const openPopper = useCallback(() => {
    setIsPopperOpen(true)
  }, [])

  const addItem = useCallback(
    (uuid: string) => {
      if (value?.includes(uuid)) return
      onChange([...(value ?? []), uuid])
    },
    [value, onChange]
  )

  const handleSelect = useCallback(
    (member: ProjectMemberSearchOption) => {
      addItem(member.uuid)
      setSearchText('')
      inputRef.current?.focus()
    },
    [addItem]
  )

  const handleSearchChange = useCallback((newValue: string) => {
    setSearchText(newValue)
    setIsPopperOpen(true)
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isPopperOpen) return
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowUp()
      } else if (event.key === 'Enter') {
        if (event.nativeEvent.isComposing) return
        event.preventDefault()
        popperRef.current?.enter()
      } else if (event.key === 'Escape') {
        closePopper()
      }
    },
    [closePopper, isPopperOpen]
  )

  const removeItem = useCallback(
    (uuid: string) => {
      if (!value) return
      const newValue = value.filter(v => v !== uuid)
      onChange(newValue.length > 0 ? newValue : undefined)
    },
    [value, onChange]
  )

  const clearAll = useCallback(() => {
    onChange(undefined)
    setSearchText('')
    inputRef.current?.focus()
  }, [onChange])

  const hasSelection = !!value && value.length > 0

  return (
    <Container>
      <TitleTypography>{title}</TitleTypography>
      <ClickAwayListener onClickAway={closePopper}>
        <ContentSpan>
          <PullDownSpan ref={anchorRef}>
            {selectedOptions.map(option => (
              <SelectedValueBox key={option.value}>
                <SelectedAvatar
                  variant="circular"
                  src={option.iconUrl || undefined}
                />
                <SelectedNameTypography>
                  {option.name || null}
                </SelectedNameTypography>
                <ChipClearButton
                  colorPattern="monotone"
                  onClick={() => removeItem(option.value)}
                >
                  <ChipClearIcon />
                </ChipClearButton>
              </SelectedValueBox>
            ))}
            <InputArea>
              <ProjectMemberInput
                inputRef={inputRef}
                value={searchText}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                onFocus={openPopper}
                autoFocus={false}
                placeholder={intl.formatMessage({
                  id: 'wbsItemSearchFilterPanel.input.select',
                })}
              />
            </InputArea>
            {isPopperOpen && (
              <ProjectMemberPopper
                ref={popperRef}
                open={isPopperOpen}
                anchorEl={anchorRef.current}
                options={displayOptions}
                onSelect={handleSelect}
                inputRef={inputRef}
                searchText={searchText}
              />
            )}
          </PullDownSpan>
          {hasSelection && (
            <ClearAllSpan>
              <ClearAllButton onClick={clearAll}>
                <ClearAllIcon />
              </ClearAllButton>
            </ClearAllSpan>
          )}
        </ContentSpan>
      </ClickAwayListener>
    </Container>
  )
}

const Container = styled('div')({
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: '5px 10px',
  minHeight: '40px',
  flexShrink: 0,
  outline: 'none',
})

const TitleTypography = styled(Typography)({
  color: colorPalette.monotone[4],
  minWidth: '80px',
  marginRight: '10px',
})

const ContentSpan = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
})

const PullDownSpan = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  maxWidth: '416px',
  flexWrap: 'wrap',
  alignItems: 'center',
  flex: 1,
})

const SelectedValueBox = styled('div')({
  display: 'flex',
  padding: '3px 5px',
  borderRadius: '15px',
  margin: '3px 5px 3px 0',
  flexWrap: 'wrap',
  background: colorPalette.monotone[1],
})

const SelectedAvatar = styled(Avatar)({
  width: 20,
  height: 20,
  margin: 'auto 5px auto 5px',
})

const SelectedNameTypography = styled(Typography)({
  margin: 'auto 5px',
  color: colorPalette.monotone[5],
})

const ChipClearButton = styled(Button)({
  border: 'none',
  background: colorPalette.monotone[0],
  width: '10px',
  height: '24px',
  borderRadius: '15px',
  cursor: 'pointer',
})

const ChipClearIcon = styled(ClearRoundedIcon)({
  color: colorPalette.monotone[4],
  height: '13px',
  width: '13px',
})

const InputArea = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: '80px',
  minHeight: '30px',
  margin: '3px 0',
})

const ClearAllSpan = styled('span')({
  display: 'flex',
  flexDirection: 'row',
  margin: 'auto 0 auto auto',
})

const ClearAllButton = styled(MuiButton)({
  minWidth: '20px',
  margin: '3px 0 3px auto',
})

const ClearAllIcon = styled(ClearRoundedIcon)({
  color: colorPalette.monotone[4],
})
